'use client';
import { useEffect, useMemo, useState } from 'react';
import {
  ColumnDef,
  functionalUpdate,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';

import {
  createFormattedColumns,
  createSelectionColumn,
  calculateColumnPinning,
} from './utils/column-helpers';
import { calculateTrophyColumns } from './utils/trophy-helpers';

interface IUseTable {
  search: string;
  onSearch: (search: string) => void;
  columns: ColumnDef<any, any>[];
  data: any[];
  includeRowSelection: boolean;
  checkboxSize?: number;
  tableState?: Record<string, any>;
  onTableStateChange?: (state: Record<string, any>) => void;
  tableConfigName?: string;
  disableSort?: boolean;
}

export const useTable = ({
  columns,
  data,
  checkboxSize,
  includeRowSelection,
  onTableStateChange,
  tableState,
  tableConfigName,
  onSearch,
  search,
  disableSort = false,
}: IUseTable) => {
  const [columnVisibility, setColumnVisibility] = useState(
    tableState?.columnVisibility || {}
  );

  useEffect(() => {
    if (tableState?.columnVisibility) {
      setColumnVisibility(tableState?.columnVisibility);
    }
  }, [tableState]);

  // Create column configuration with optional selection column
  const columnConfig: ColumnDef<any>[] = useMemo(() => {
    const selectionColumns = includeRowSelection
      ? [createSelectionColumn(checkboxSize)]
      : [];

    const formattedColumns = createFormattedColumns(columns);

    return [...selectionColumns, ...formattedColumns];
  }, [columns, includeRowSelection]);

  // Calculate column pinning using the extracted utility
  const columnPinning = useMemo(() => {
    return calculateColumnPinning(columns);
  }, [columns]);

  // Configure the table
  const table = useReactTable<any>({
    data,
    columns: columnConfig,
    state: {
      globalFilter: search,
      columnVisibility,
      columnPinning,
    },
    getSortedRowModel: getSortedRowModel(),
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: (newVisibility) => {
      setColumnVisibility(newVisibility);

      const value = functionalUpdate(newVisibility, columnVisibility);

      if (onTableStateChange && tableConfigName) {
        onTableStateChange({ [tableConfigName]: { columnVisibility: value } });
      }
    },
    onGlobalFilterChange: onSearch,
    enableSorting: !disableSort,
  });

  // Calculate trophy columns using the extracted utility
  const trophyColumns = useMemo(() => {
    return calculateTrophyColumns(columns, data);
  }, [data, columns]);

  return { table, trophyColumns };
};
